<template>
	<div class="modal-overlay" @click="$emit('close')">
		<div class="modal-content" @click.stop>
			<button class="modal-close" @click="$emit('close')">
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
					<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
				</svg>
			</button>
			
			<h2>Двухфакторная аутентификация</h2>
			<p class="mfa-description">
				Введите 6-значный код из приложения для аутентификации или используйте резервный код.
			</p>

			<input
				v-model="code"
				type="text"
				maxlength="20"
				placeholder="Введите код"
				class="mfa-input"
				@input="validateCode"
				@keyup.enter="verify"
				autofocus
			/>

			<div class="modal-actions">
				<button class="cancel-btn" @click="$emit('close')">
					Отмена
				</button>
				<button
					class="verify-btn"
					:disabled="!isCodeValid || isVerifying"
					@click="verify"
				>
					{{ isVerifying ? 'Проверка...' : 'Войти' }}
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { authService } from '@/services/auth'
// import { useAppStore } from '@/stores/useAppStore'

const props = defineProps({
	userId: {
		type: Number,
		required: true,
	},
	email: {
		type: String,
		required: true,
	},
})

const emit = defineEmits(['close', 'success'])

const code = ref('')
const isVerifying = ref(false)

const isCodeValid = computed(() => {
	const trimmed = code.value.trim()
	// 6 цифр или резервный код (8 символов)
	return /^\d{6}$/.test(trimmed) || /^[A-Z0-9]{8}$/i.test(trimmed)
})

const validateCode = () => {
	// Автоматически преобразуем в верхний регистр для резервных кодов
	if (code.value.length > 6) {
		code.value = code.value.toUpperCase()
	}
}

const verify = async () => {
	if (!isCodeValid.value || isVerifying.value) return

	isVerifying.value = true

	try {
		await authService.verifyMfaLogin(props.userId, code.value.trim())
		emit('success')
	} catch (error) {
		console.error('Ошибка МФА:', error)
		// Ошибка уже показана в authService
	} finally {
		isVerifying.value = false
	}
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.7);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10000;
	animation: fadeIn 0.2s;
}

@keyframes fadeIn {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

.modal-content {
	background: #1a1a2e;
	border-radius: 16px;
	padding: 32px;
	max-width: 480px;
	width: 90%;
	position: relative;
	animation: slideUp 0.3s;
}

@keyframes slideUp {
	from {
		transform: translateY(20px);
		opacity: 0;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

.modal-close {
	position: absolute;
	top: 16px;
	right: 16px;
	background: none;
	border: none;
	color: #a0aec0;
	cursor: pointer;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: color 0.2s;
	border-radius: 8px;
}

.modal-close:hover {
	color: #fff;
	background: rgba(255, 255, 255, 0.1);
}

h2 {
	font-size: 24px;
	font-weight: 600;
	color: #fff;
	margin: 0 0 12px 0;
}

.mfa-description {
	font-size: 14px;
	color: #a0aec0;
	margin: 0 0 24px 0;
	line-height: 1.5;
}

.mfa-input {
	width: 100%;
	padding: 14px 16px;
	border: 1px solid #2d3748;
	border-radius: 8px;
	font-size: 16px;
	background: #0f0f1e;
	color: #fff;
	transition: all 0.2s;
	margin-bottom: 24px;
	text-align: center;
	letter-spacing: 4px;
	font-weight: 500;
}

.mfa-input:focus {
	outline: none;
	border-color: #667eea;
	box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.mfa-input::placeholder {
	color: #4a5568;
	letter-spacing: normal;
}

.modal-actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
}

.cancel-btn,
.verify-btn {
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
}

.cancel-btn {
	background: #2d3748;
	color: #fff;
}

.cancel-btn:hover {
	background: #4a5568;
}

.verify-btn {
	background: #667eea;
	color: white;
}

.verify-btn:hover:not(:disabled) {
	background: #5568d3;
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.verify-btn:disabled {
	background: #4a5568;
	cursor: not-allowed;
	opacity: 0.6;
}
</style>
